import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import {
  FaCalendarAlt,
  FaEdit,
  FaRupeeSign,
  FaSave,
  FaTag,
  FaUser,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/class-settings-modals.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const PAYMENT_MODES = [
  "Cash",
  "Cheque",
  "Bank Transfer",
  "UPI",
  "Card",
  "Other",
];

//Created By Cursor
function parseLooseDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

//Created By Cursor
function toInputDate(raw) {
  const d = parseLooseDate(raw);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

//Created By Cursor
function clampDateToRange(isoDate, minIso, maxIso) {
  if (!isoDate) return isoDate;
  if (minIso && isoDate < minIso) return minIso;
  if (maxIso && isoDate > maxIso) return maxIso;
  return isoDate;
}

//Created By Cursor
const MdlEditExpense = ({
  isOpen,
  onClose,
  entry,
  sessionId,
  sessionStartDate,
  sessionEndDate,
  onSuccess,
}) => {
  const [form, setForm] = useState({
    date: "",
    amount: "",
    payment_mode: "Cash",
    reference_no: "",
    remark: "",
  });
  const [categoryList, setCategoryList] = useState([]);
  const [partyList, setPartyList] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [partyId, setPartyId] = useState("");
  const [categoryInput, setCategoryInput] = useState("");
  const [partyInput, setPartyInput] = useState("");
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [showPartyInput, setShowPartyInput] = useState(false);

  //Created By Cursor
  const getSessionBounds = useCallback(() => {
    const fromSession = SessionManager.getCurrentSession()?.[0];
    const minIso =
      toInputDate(sessionStartDate) ||
      toInputDate(fromSession?.start_date) ||
      "";
    const maxIso =
      toInputDate(sessionEndDate) || toInputDate(fromSession?.end_date) || "";
    return { minIso, maxIso };
  }, [sessionStartDate, sessionEndDate]);

  //Created By Cursor
  const fetchCategoriesAndParties = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;
    try {
      setLoadingMeta(true);
      const response = await axios.get(`${API_BASE()}getDaybookAddExpenseData.php`, {
        params: { school_code: schoolCode },
      });
      if (response.data?.api_status === "OK") {
        const data = response.data.data || {};
        setCategoryList(data.DaybookCategory || []);
        setPartyList(data.DaybookParty || []);
      }
    } catch (error) {
      console.error("[MdlEditExpense] meta fetch error", error);
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !entry) return;
    const { minIso, maxIso } = getSessionBounds();
    const date = clampDateToRange(toInputDate(entry.entry_date), minIso, maxIso);
    setForm({
      date: date || "",
      amount: entry.amount != null ? String(entry.amount) : "",
      payment_mode: entry.payment_mode || "Cash",
      reference_no: entry.reference_no || "",
      remark: entry.remark || "",
    });
    setCategoryId(entry.category_id || "");
    setPartyId(entry.party_id || "");
    setCategoryInput(entry.category_name || "");
    setPartyInput(entry.party_name || "");
    setShowCategoryInput(false);
    setShowPartyInput(false);
    fetchCategoriesAndParties();
  }, [isOpen, entry, getSessionBounds, fetchCategoriesAndParties]);

  //Created By Cursor
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  //Created By Cursor
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "__NEW__") {
      setShowCategoryInput(true);
      setCategoryId("");
      setCategoryInput("");
    } else {
      const selected = categoryList.find((c) => c.category_id === value);
      setShowCategoryInput(false);
      setCategoryId(value);
      setCategoryInput(selected?.category_name || "");
    }
  };

  //Created By Cursor
  const handlePartyChange = (e) => {
    const value = e.target.value;
    if (value === "__NEW__") {
      setShowPartyInput(true);
      setPartyId("");
      setPartyInput("");
    } else {
      const selected = partyList.find((p) => p.party_id === value);
      setShowPartyInput(false);
      setPartyId(value);
      setPartyInput(selected?.party_name || "");
    }
  };

  //Created By Cursor
  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const schoolCode = SessionManager.getSchoolCode();
    const staffId = SessionManager.getStaffDetails()?.staff_id || "";
    const entryId = String(entry?.entry_id || "").trim();

    if (!sessionId || !schoolCode || !staffId || !entryId) {
      myToast.notify_warning("Session or staff details missing.");
      return;
    }
    if (!form.date || !form.amount) {
      myToast.notify_warning("Please fill date and amount.");
      return;
    }

    const categoryName = showCategoryInput
      ? categoryInput.trim()
      : categoryInput.trim() ||
        categoryList.find((c) => c.category_id === categoryId)?.category_name ||
        "";
    if (!categoryId && !categoryName) {
      myToast.notify_warning("Please select or enter a category.");
      return;
    }

    const { minIso, maxIso } = getSessionBounds();
    if (minIso && form.date < minIso) {
      myToast.notify_warning(`Date cannot be before session start (${minIso}).`);
      return;
    }
    if (maxIso && form.date > maxIso) {
      myToast.notify_warning(`Date cannot be after session end (${maxIso}).`);
      return;
    }

    const partyName = showPartyInput
      ? partyInput.trim()
      : partyInput.trim() ||
        partyList.find((p) => p.party_id === partyId)?.party_name ||
        "";

    setSaving(true);
    try {
      const res = await axios.post(
        `${API_BASE()}updateDayBookEntry.php`,
        {
          staff_id: staffId,
          session_id: sessionId,
          entry_id: entryId,
          amount: String(form.amount),
          entry_date: form.date,
          category_id: showCategoryInput ? "" : categoryId || "",
          category_name: categoryName,
          party_id: showPartyInput ? "" : partyId || "",
          party_name: partyName,
          payment_mode: form.payment_mode || "Cash",
          reference_no: form.reference_no || "",
          remark: form.remark || "",
          school_code: schoolCode,
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (res.data?.api_status === "OK") {
        myToast.notify_success("Entry updated.");
        if (typeof onSuccess === "function") onSuccess();
        onClose();
      } else {
        myToast.notify_warning(res.data?.api_remark || "Could not update entry.");
      }
    } catch (error) {
      console.error("[MdlEditExpense] save error", error);
      myToast.notify_warning("Failed to update entry.");
    } finally {
      setSaving(false);
    }
  };

  const sessionBounds = getSessionBounds();
  const entryType = String(entry?.entry_type || "").toUpperCase();
  const typeLabel = entryType === "CASH_IN" ? "Cash in" : "Cash out";

  return (
    <Modal
      show={Boolean(isOpen)}
      onHide={onClose}
      animation={false}
      size="lg"
      centered
      dialogClassName="cls-mdl-root em-mdl-root"
      aria-labelledby="edit-expense-title"
    >
      <Modal.Header closeButton>
        <div className="em-mdl-header-row">
          <div className="em-mdl-header-brand">
            <span className="em-mdl-header-icon" aria-hidden>
              <FaEdit />
            </span>
            <div>
              <Modal.Title id="edit-expense-title">Edit Entry</Modal.Title>
              <span className="cls-mdl-sub">
                {typeLabel}
                {entry?.entry_id ? ` · ${entry.entry_id}` : ""}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="em-mdl-header-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loadingMeta && categoryList.length === 0 ? (
          <MyLoader />
        ) : (
          <form id="em-edit-expense-form" onSubmit={handleSave}>
            <div className="em-mdl-section">
              <div className="em-mdl-section__head">
                <span>01</span>
                <div>
                  <strong>Expense details</strong>
                  <em>Date, amount, category and party</em>
                </div>
              </div>
              <div className="cls-mdl-panel em-mdl-panel">
                <div className="cls-mdl-grid">
                  <label className="cls-mdl-field">
                    <span>
                      <FaCalendarAlt aria-hidden /> Date *
                    </span>
                    <input
                      type="date"
                      name="date"
                      value={form.date}
                      onChange={handleChange}
                      min={sessionBounds.minIso || undefined}
                      max={sessionBounds.maxIso || undefined}
                      required
                      disabled={saving}
                    />
                  </label>

                  <label className="cls-mdl-field">
                    <span>
                      <FaRupeeSign aria-hidden /> Amount *
                    </span>
                    <div className="em-mdl-amount">
                      <span className="em-mdl-amount__prefix" aria-hidden>
                        ₹
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="amount"
                        placeholder="0.00"
                        value={form.amount}
                        onChange={handleChange}
                        required
                        disabled={saving}
                      />
                    </div>
                  </label>

                  <label className="cls-mdl-field">
                    <span>
                      <FaTag aria-hidden /> Category *
                    </span>
                    <select
                      value={categoryId || (showCategoryInput ? "__NEW__" : "")}
                      onChange={handleCategoryChange}
                      disabled={saving}
                    >
                      <option value="">Select Category</option>
                      {categoryList.map((cat) => (
                        <option key={cat.category_id} value={cat.category_id}>
                          {cat.category_name}
                        </option>
                      ))}
                      <option value="__NEW__">+ Add New Category</option>
                    </select>
                  </label>

                  <label className="cls-mdl-field">
                    <span>
                      <FaUser aria-hidden /> Party
                    </span>
                    <select
                      value={partyId || (showPartyInput ? "__NEW__" : "")}
                      onChange={handlePartyChange}
                      disabled={saving}
                    >
                      <option value="">Select Party</option>
                      {partyList.map((p) => (
                        <option key={p.party_id} value={p.party_id}>
                          {p.party_name}
                        </option>
                      ))}
                      <option value="__NEW__">+ Add New Party</option>
                    </select>
                  </label>

                  {showCategoryInput ? (
                    <label className="cls-mdl-field full">
                      <span>New category name *</span>
                      <input
                        type="text"
                        placeholder="Enter category"
                        value={categoryInput}
                        onChange={(e) => setCategoryInput(e.target.value)}
                        required
                        disabled={saving}
                      />
                    </label>
                  ) : null}

                  {showPartyInput ? (
                    <label className="cls-mdl-field full">
                      <span>New party name</span>
                      <input
                        type="text"
                        placeholder="Enter party"
                        value={partyInput}
                        onChange={(e) => setPartyInput(e.target.value)}
                        disabled={saving}
                      />
                    </label>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="em-mdl-section em-mdl-section--compact">
              <div className="em-mdl-section__head">
                <span>02</span>
                <div>
                  <strong>Payment</strong>
                  <em>Mode, reference and notes</em>
                </div>
              </div>
              <div className="cls-mdl-panel em-mdl-panel em-mdl-panel--compact">
                <div className="em-mdl-pay-compact">
                  <div className="cls-mdl-field em-mdl-pay-field">
                    <span>Mode</span>
                    <div
                      className="em-mdl-pay-chips"
                      role="group"
                      aria-label="Payment mode"
                    >
                      {PAYMENT_MODES.map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          className={`em-mdl-pay-chip${
                            form.payment_mode === mode ? " is-active" : ""
                          }`}
                          onClick={() =>
                            setForm((prev) => ({ ...prev, payment_mode: mode }))
                          }
                          disabled={saving}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="cls-mdl-field">
                    <span>Reference</span>
                    <input
                      type="text"
                      name="reference_no"
                      placeholder="Cheque / UPI / Txn ID"
                      value={form.reference_no}
                      onChange={handleChange}
                      disabled={saving}
                    />
                  </label>

                  <label className="cls-mdl-field">
                    <span>Notes</span>
                    <input
                      type="text"
                      name="remark"
                      placeholder="Optional notes"
                      value={form.remark}
                      onChange={handleChange}
                      disabled={saving}
                    />
                  </label>
                </div>
              </div>
            </div>
          </form>
        )}
      </Modal.Body>

      <Modal.Footer className="em-mdl-footer">
        <Button
          className="cls-mdl-btn-ghost"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          className="em-mdl-save"
          type="submit"
          form="em-edit-expense-form"
          disabled={saving || loadingMeta}
        >
          <span className="em-mdl-save__icon" aria-hidden>
            <FaSave />
          </span>
          <span>{saving ? "Saving…" : "Save Changes"}</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MdlEditExpense;
